// src/components/admin/AdminHeader.tsx
import React, { useEffect, useState } from 'react';
import { Box, IconButton, Avatar, Typography, Menu, MenuItem, Divider, ListItemIcon, Badge, List, ListItem, ListItemText } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface AdminHeaderProps {
  onToggleMenu: () => void;
}

// Normalize role for color coding (same as in SlideMenu)
const normalizeRole = (role: string): string => {
  const r = role?.toLowerCase().trim() || '';
  if (!r || r === 'new' || r === 'unknown') return 'admin';
  if (r.includes('super')) return 'super_admin';
  if (r === 'operation' || r === 'vendor') return r;
  return 'admin';
};

// Map normalized role to color — kept in sync with SlideMenu's role chip so
// the same role always shows the same color across the whole admin panel.
// Super Admin uses the actual VOTO brand orange-dark rather than a generic
// color, since it's the role shown by default in the admin experience.
const getRoleColor = (normalizedRole: string): string => {
  switch (normalizedRole) {
    case 'super_admin': return 'var(--orange-dark, #e94c1c)'; // Brand orange
    case 'admin':       return '#2196f3'; // Blue
    case 'operation':   return '#ff9800'; // Amber
    case 'vendor':      return '#4caf50'; // Green
    default:            return 'var(--orange, #F76338)'; // Brand orange (fallback)
  }
};

const AdminHeader: React.FC<AdminHeaderProps> = ({ onToggleMenu }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [lastNotificationCheck, setLastNotificationCheck] = useState<string>(() => new Date(Date.now() - 60 * 1000).toISOString());
  const [notificationUnread, setNotificationUnread] = useState(0);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  // --- Get user info from localStorage (keys set in adminLogin.tsx) ---
  const isVendor = (localStorage.getItem('user_role') || '').toLowerCase() === 'vendor';
  const userName  = (isVendor ? localStorage.getItem('user_name') : localStorage.getItem('admin_name')) || 'Admin';
  const userEmail = (isVendor ? localStorage.getItem('user_email') : localStorage.getItem('admin_email')) || 'admin@example.com';

  // Raw role from API (preserved under 'role')
  const rawRole = localStorage.getItem('role') || localStorage.getItem('user_role') || 'admin';

  // Normalized role for color coding
  const normalizedRole = normalizeRole(rawRole);

  // Avatar letter
  const avatarLetter = userName.charAt(0).toUpperCase() || userEmail.charAt(0).toUpperCase();

  // Format raw role for display (capitalize first letter)
  const displayRole = rawRole
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  useEffect(() => {
    if (isVendor) return;
    let active = true;
    const loadNotifications = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        if (!token) return;
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_ENDPOINT}/admin/new-order-notifications?since=${encodeURIComponent(lastNotificationCheck)}`,
          { headers: { Authorization: `Bearer ${token}`, authkey: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3' } }
        );
        const data = await response.json();
        if (active && !data.error) {
          setNotifications(prev => {
            const incoming = data.notifications || [];
            const previousIds = new Set(prev.map((n:any) => n.id));
            const fresh = incoming.filter((n:any) => !previousIds.has(n.id));
            if (fresh.length) {
              setNotificationUnread(v => Math.min(99, v + fresh.length));
              fresh.slice(0, 3).forEach((n:any) => toast.info(`New order: ${n.title || n.message || 'New order received'}`, { autoClose: 5000 }));
            }
            const map = new Map(prev.map((n:any) => [n.id, n]));
            incoming.forEach((n:any) => map.set(n.id, n));
            return Array.from(map.values()).sort((a:any,b:any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 50);
          });
          setLastNotificationCheck(new Date().toISOString());
        }
      } catch (err) {
        console.warn('Admin notification polling failed', err);
      }
    };
    loadNotifications();
    const timer = window.setInterval(loadNotifications, 5000);
    return () => { active = false; window.clearInterval(timer); };
  }, [isVendor]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/adminProfile');
  };

  const handleSettings = () => {
    handleClose();
    navigate('/globalSettings');
  };

  const handleLogout = () => {
    const wasVendor = (localStorage.getItem('user_role') || '').toLowerCase() === 'vendor';

    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_name');
    localStorage.removeItem('admin_type');
    localStorage.removeItem('role');
    localStorage.removeItem('admin_role_id');
    localStorage.removeItem('admin_permissions');
    localStorage.removeItem('admin_perm_map');
    localStorage.removeItem('vendor_token');
    localStorage.removeItem('vendor_id');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_id');

    handleClose();
    navigate(wasVendor ? '/vendorlogin' : '/adminLogin');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '71px',
        px: 2,
        backgroundColor: 'var(--white, #FFFFFF)',
        borderBottom: '1px solid var(--border, #E8E8E8)',
        boxShadow: 'var(--shadow-sm, 0 2px 12px rgba(0,0,0,0.06))',
        fontFamily: 'var(--ff-body, "Montserrat", sans-serif)',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      {/* Left Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={onToggleMenu}
          sx={{
            display: { xs: 'flex', md: 'none' },
            color: 'var(--dark, #1A1A1A)',
            transition: 'background-color var(--transition, 0.28s cubic-bezier(0.4,0,0.2,1))',
            '&:hover': { backgroundColor: 'rgba(247,99,56,0.08)' },
          }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
            fontWeight: 700,
            color: 'var(--dark, #1A1A1A)',
            fontSize: '16px',
            letterSpacing: '-0.01em',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          Admin Panel
        </Typography>
      </Box>

      {/* Right Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Role Badge - shows raw role name */}
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            backgroundColor: getRoleColor(normalizedRole),
            borderRadius: '20px',
            boxShadow: 'var(--shadow-sm, 0 2px 12px rgba(0,0,0,0.06))',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'white',
              fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
              fontWeight: 700,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {displayRole}
          </Typography>
        </Box>

        {!isVendor && (
          <>
            <IconButton onClick={(e) => { setNotificationAnchor(e.currentTarget); setNotificationUnread(0); }} aria-label="Notifications">
              <Badge badgeContent={notificationUnread || notifications.length} color="error" max={99}>
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>
            <Menu
              anchorEl={notificationAnchor}
              open={Boolean(notificationAnchor)}
              onClose={() => setNotificationAnchor(null)}
              PaperProps={{ sx: { width: 360, maxHeight: 460, borderRadius: 2 } }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                <Typography fontWeight={700}>New Orders</Typography>
              </Box>
              <List dense>
                {notifications.length === 0 ? (
                  <ListItem><ListItemText primary="No new orders in the last 24 hours." /></ListItem>
                ) : notifications.slice(0, 10).map((n:any) => (
                  <ListItem key={n.id} button onClick={() => { setNotificationAnchor(null); navigate('/adminOrderManagement'); }}>
                    <ListItemText primary={n.title} secondary={n.message} />
                  </ListItem>
                ))}
              </List>
            </Menu>
          </>
        )}

        <Avatar
          onClick={handleClick}
          sx={{
            width: 32,
            height: 32,
            backgroundColor: getRoleColor(normalizedRole),
            fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
            fontSize: '14px',
            fontWeight: 700,
            ml: 1,
            cursor: 'pointer',
            boxShadow: 'var(--shadow-sm, 0 2px 12px rgba(0,0,0,0.06))',
            transition: 'opacity var(--transition, 0.28s cubic-bezier(0.4,0,0.2,1))',
            '&:hover': { opacity: 0.85 },
          }}
        >
          {avatarLetter}
        </Avatar>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              minWidth: 240,
              borderRadius: 'var(--radius, 14px)',
              boxShadow: 'var(--shadow-md, 0 8px 32px rgba(0,0,0,0.10))',
              fontFamily: 'var(--ff-body, "Montserrat", sans-serif)',
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
                fontFamily: 'var(--ff-body, "Montserrat", sans-serif)',
                fontSize: '14px',
                transition: 'background-color var(--transition, 0.28s cubic-bezier(0.4,0,0.2,1))',
                '&:hover': {
                  backgroundColor: 'rgba(247,99,56,0.06)',
                },
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Profile Info Section */}
          <Box sx={{ px: 2, py: 1.5, cursor: 'default' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
                fontWeight: 700, 
                color: 'var(--dark, #1A1A1A)',
                mb: 0.5,
              }}
            >
              {userName}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'var(--gray, #6B6B6B)',
                display: 'block',
                mb: 0.5,
              }}
            >
              {userEmail}
            </Typography>
            <Box
              sx={{
                display: 'inline-block',
                px: 1,
                py: 0.25,
                backgroundColor: getRoleColor(normalizedRole),
                borderRadius: '20px',
                mt: 0.5,
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'white',
                  fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
                  fontWeight: 700,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {displayRole}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1, borderColor: 'var(--border, #E8E8E8)' }} />

          {/* Menu Items */}
          <MenuItem onClick={handleProfile}>
            <ListItemIcon>
              <PersonOutlineIcon fontSize="small" sx={{ color: 'var(--dark, #1A1A1A)' }} />
            </ListItemIcon>
            Your Profile
          </MenuItem>

          <MenuItem onClick={handleSettings}>
            <ListItemIcon>
              <SettingsOutlinedIcon fontSize="small" sx={{ color: 'var(--dark, #1A1A1A)' }} />
            </ListItemIcon>
            Settings
          </MenuItem>

          <Divider sx={{ my: 1, borderColor: 'var(--border, #E8E8E8)' }} />

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: '#d72c0d' }} />
            </ListItemIcon>
            <Typography sx={{ color: '#d72c0d', fontWeight: 600, fontFamily: 'var(--ff-body, "Montserrat", sans-serif)' }}>
              Logout
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default AdminHeader;